import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CryptoError, decodeLinkKey, fromBase64Url, inspectDrop, openDrop, sealedDropDataBytes, toBase64Url, type DropManifest, type DropSession } from '@sss/crypto';
import FlameMark from '../components/Brand/FlameMark';
import secretStyles from '../components/Secret/Secret.module.css';
import { usePageTitle } from '../hooks/usePageTitle';
import { downloadRecords } from '../lib/dropDownload';
import { claimDrop, finishDropDownload, getDropMetadata, readDropRange } from '../lib/dropsApi';
import { forgetDownloadResume, loadDownloadResume, saveDownloadResume, type DownloadResume } from '../lib/dropResume';
import { askPersistentStorage, canSaveToDisk, canSaveToOpfs, clearOpfs, freeStorage, memorySink, opfsSink, pickDiskSink, type FileSink, type SavedFile } from '../lib/fileSink';
import { forgetFragment, readFragment } from '../lib/secretLinks';
import { SecretsApiError } from '../lib/secretsApi';
import { formatBytes, safeFileName } from '../lib/tools';
import ui from '../styles/ui.module.css';
import styles from './LivePage.module.css';
import revealStyles from './RevealPage.module.css';

/**
 * /d/:id#<linkKey> — collect a stored file drop (docs/adr/0015). Opening the
 * page reads the header and sealed manifest and shows the files; it never
 * uses a download. Download does: it picks where to save, claims a download
 * (a 24-hour window), reads every record by Range, checks it, and writes it.
 * A reload in the window carries on without using another download.
 */

/** Above this, a browser that can only keep files in memory is warned. */
const MEMORY_WARNING_BYTES = 500 * 1024 * 1024;
/** OPFS needs room for the file, and a little over, before the copy to Downloads. */
const OPFS_HEADROOM = 1.1;

type Opened = { manifest: DropManifest; session: DropSession; endsAt: string | null; downloadsLeft: number };

type State =
  | { step: 'checking' }
  | { step: 'incomplete' }
  | { step: 'gone' }
  | { step: 'uploading' }
  | { step: 'passphrase'; header: Uint8Array; sealedManifest: Uint8Array; endsAt: string | null; downloadsLeft: number; wrong: boolean }
  | ({ step: 'ready'; noSpace: boolean } & Opened)
  | ({ step: 'downloading'; done: number; retryIn: number | null } & Opened)
  | { step: 'saved'; saved: SavedFile[]; disk: boolean }
  | { step: 'failed'; reason: 'gone' | 'tampered' | 'noDownloads' | 'network' | 'other' };

export default function DropReceivePage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const [state, setState] = useState<State>({ step: 'checking' });
  const [passphrase, setPassphrase] = useState('');
  const linkKey = useRef<Uint8Array | null>(null);
  const resume = useRef<DownloadResume | null>(null);
  const controller = useRef<AbortController | null>(null);
  const urls = useRef<string[]>([]);
  const heading = useRef<HTMLHeadingElement>(null);
  usePageTitle(t('pageTitle.drop'));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // A reload during a download: the key is in the resume record, not the address bar.
      resume.current = await loadDownloadResume(id);
      const fragment = readFragment(window.location.hash);
      try {
        linkKey.current = fragment ? decodeLinkKey(fragment) : resume.current ? decodeLinkKey(resume.current.linkKey) : null;
      } catch {
        linkKey.current = null;
      }
      if (!linkKey.current) return !cancelled && setState({ step: 'incomplete' });
      let meta;
      try {
        meta = await getDropMetadata(id);
      } catch (err) {
        if (cancelled) return;
        return setState(err instanceof SecretsApiError && err.status === 404 ? { step: 'gone' } : { step: 'failed', reason: 'network' });
      }
      if (cancelled) return;
      if (meta.state === 'uploading') return setState({ step: 'uploading' });
      const header = fromBase64Url(meta.header);
      const sealedManifest = fromBase64Url(meta.sealedManifest);
      let hasPassphrase;
      try {
        hasPassphrase = inspectDrop(header).hasPassphrase;
      } catch {
        return setState({ step: 'failed', reason: 'other' });
      }
      if (hasPassphrase) return setState({ step: 'passphrase', header, sealedManifest, endsAt: meta.endsAt, downloadsLeft: meta.downloadsLeft, wrong: false });
      await open(header, sealedManifest, undefined, meta.endsAt, meta.downloadsLeft, () => cancelled);
    })();
    // Files kept in OPFS by an earlier visit are of no use now.
    void clearOpfs();
    return () => {
      cancelled = true;
      controller.current?.abort(new Error('left'));
      for (const url of urls.current) URL.revokeObjectURL(url);
      void clearOpfs();
    };
  }, [id]);

  useEffect(() => {
    if (state.step === 'saved' || state.step === 'ready') heading.current?.focus();
  }, [state.step]);

  /**
   * Open the manifest and show the files. `superseded` says this load was replaced (the page was left, or
   * React ran the effect again): its result must not land on top of a download that has already started.
   */
  async function open(header: Uint8Array, sealedManifest: Uint8Array, pass: string | undefined, endsAt: string | null, downloadsLeft: number, superseded = () => false) {
    try {
      const { manifest, session } = await openDrop({ header, sealedManifest, linkKey: linkKey.current as Uint8Array, passphrase: pass });
      if (superseded()) return;
      setState({ step: 'ready', manifest, session, endsAt, downloadsLeft, noSpace: false });
    } catch (err) {
      if (superseded()) return;
      if (pass !== undefined && err instanceof CryptoError && err.code === 'decryptionFailed') {
        setState({ step: 'passphrase', header, sealedManifest, endsAt, downloadsLeft, wrong: true });
      } else {
        setState({ step: 'failed', reason: 'tampered' });
      }
    }
  }

  /** Where the files will go: straight to disk, into OPFS, or memory. Null if the person cancels or there's no room. */
  async function chooseSink(opened: Opened): Promise<FileSink | 'noSpace' | null> {
    if (canSaveToDisk()) return pickDiskSink(opened.manifest.files);
    if (canSaveToOpfs()) {
      const needed = opened.session.dataBytes * OPFS_HEADROOM;
      let free = await freeStorage();
      // Firefox allows 10 GiB without asking; persistent storage raises it, with a prompt.
      if (free !== null && free < needed && (await askPersistentStorage())) free = await freeStorage();
      if (free !== null && free < needed) return 'noSpace';
      return (await opfsSink(id)) ?? memorySink();
    }
    return memorySink();
  }

  async function download(opened: Opened) {
    const sink = await chooseSink(opened);
    if (sink === null) return;
    if (sink === 'noSpace') return setState({ ...opened, step: 'ready', noSpace: true });
    let token = resume.current?.downloadToken;
    if (!token) {
      try {
        const claim = await claimDrop(id);
        token = claim.downloadToken;
        resume.current = { id, linkKey: toBase64Url(linkKey.current as Uint8Array), downloadToken: token, windowEndsAt: claim.windowEndsAt };
        await saveDownloadResume(resume.current);
      } catch (err) {
        await sink.abort();
        return setState({ step: 'failed', reason: err instanceof SecretsApiError && err.status === 410 ? 'noDownloads' : err instanceof SecretsApiError && err.status === 404 ? 'gone' : 'network' });
      }
    }
    // The key is in use and kept for a reload; it needn't sit in the address bar any more.
    forgetFragment();
    const abort = new AbortController();
    controller.current = abort;
    // `opened` is the ready state itself: its step must not win.
    setState({ ...opened, step: 'downloading', done: 0, retryIn: null });
    const downloadToken = token;
    try {
      const saved = await downloadRecords({
        manifest: opened.manifest,
        session: opened.session,
        sink,
        read: (start, end, signal) => readDropRange(id, downloadToken, start, end, signal),
        onRecords: (done) => setState((s) => (s.step === 'downloading' ? { ...s, done } : s)),
        onRetry: (retryIn) => setState((s) => (s.step === 'downloading' ? { ...s, retryIn } : s)),
        signal: abort.signal,
      });
      urls.current.push(...saved.flatMap((f) => (f.url ? [f.url] : [])));
      await finishDropDownload(id, downloadToken).catch(() => undefined);
      await forgetDownloadResume(id);
      setState({ step: 'saved', saved, disk: sink.kind === 'disk' && canSaveToDisk() });
    } catch (err) {
      if (abort.signal.aborted) return;
      setState({ step: 'failed', reason: err instanceof CryptoError ? 'tampered' : err instanceof SecretsApiError && err.type === 'gone' ? 'gone' : 'other' });
    }
  }

  const title = (key: string) => (
    <h1 className={revealStyles.title} tabIndex={-1} ref={heading}>
      {t(key)}
    </h1>
  );

  const body = (() => {
    switch (state.step) {
      case 'checking':
        return <p className={ui.hint}>{t('dropReceive.checking')}</p>;
      case 'incomplete':
        return (
          <>
            {title('dropReceive.incomplete.title')}
            <p>{t('dropReceive.incomplete.body')}</p>
          </>
        );
      case 'gone':
        return (
          <>
            {title('dropReceive.gone.title')}
            <p>{t('dropReceive.gone.body')}</p>
          </>
        );
      case 'uploading':
        return (
          <>
            {title('dropReceive.uploading.title')}
            <p>{t('dropReceive.uploading.body')}</p>
            <button type="button" className={ui.buttonSecondary} onClick={() => window.location.reload()}>
              {t('dropReceive.uploading.again')}
            </button>
          </>
        );
      case 'passphrase':
        return (
          <form
            className={secretStyles.actions}
            onSubmit={(e) => {
              e.preventDefault();
              void open(state.header, state.sealedManifest, passphrase, state.endsAt, state.downloadsLeft);
            }}
          >
            {title('dropReceive.passphrase.title')}
            <label className={ui.field}>
              {t('dropReceive.passphrase.label')}
              <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="off" aria-invalid={state.wrong || undefined} />
            </label>
            {state.wrong && (
              <p role="alert" className={ui.error}>
                {t('dropReceive.passphrase.wrong')}
              </p>
            )}
            <button type="submit" className={ui.button}>
              {t('dropReceive.passphrase.unlock')}
            </button>
          </form>
        );
      case 'ready': {
        const ends = state.endsAt ? new Date(state.endsAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }) : '';
        const big = !canSaveToDisk() && state.session.dataBytes > MEMORY_WARNING_BYTES;
        return (
          <>
            {title('dropReceive.ready.title')}
            <p className={ui.hint}>{t('dropReceive.ready.meta', { count: state.manifest.files.length, size: formatBytes(state.session.dataBytes), when: ends })}</p>
            <ul className={styles.fileList}>
              {state.manifest.files.map((f, i) => (
                <li key={i} className={styles.fileItem}>
                  <span className={styles.fileName}>{safeFileName(f.name)}</span>
                  <span className={ui.hint}>{formatBytes(f.size)}</span>
                </li>
              ))}
            </ul>
            <p className={secretStyles.notice}>
              {resume.current ? t('dropReceive.ready.resume') : t('dropReceive.ready.uses', { count: state.downloadsLeft })}
            </p>
            {big && <p className={ui.hint}>{t('dropReceive.ready.memory')}</p>}
            {state.noSpace && (
              <p role="alert" className={ui.error}>
                {t('dropReceive.ready.noSpace', { size: formatBytes(state.session.dataBytes * OPFS_HEADROOM) })}
              </p>
            )}
            <button type="button" className={ui.button} onClick={() => void download(state)}>
              <FlameMark size={18} tone="onAccent" />
              {resume.current ? t('dropReceive.ready.resumeButton') : t('dropReceive.ready.download')}
            </button>
          </>
        );
      }
      case 'downloading': {
        const total = state.session.totalRecords;
        const percent = total > 0 ? Math.floor((state.done / total) * 100) : 100;
        return (
          <>
            {title('dropReceive.downloading.title')}
            <p className={secretStyles.notice}>{t('dropReceive.downloading.keepOpen')}</p>
            <progress className={styles.progress} value={state.done} max={Math.max(total, 1)} aria-label={t('dropReceive.downloading.progress')} />
            <p role="status" className={ui.hint} data-testid="drop-download-progress">
              {state.retryIn !== null
                ? t('dropReceive.downloading.retrying', { percent, seconds: state.retryIn })
                : t('dropReceive.downloading.line', { percent, total: formatBytes(sealedDropDataBytes(total)) })}
            </p>
          </>
        );
      }
      case 'saved':
        return (
          <>
            {title('dropReceive.saved.title')}
            <p>{t(state.disk ? 'dropReceive.saved.disk' : 'dropReceive.saved.memory')}</p>
            {!state.disk && (
              <ul className={styles.fileList}>
                {state.saved.map((f, i) => (
                  <li key={i} className={styles.fileItem}>
                    <a href={f.url} download={f.name} className={ui.buttonSecondary}>
                      {t('files.saveNamed', { name: f.name })}
                    </a>
                    <span className={ui.hint}>{formatBytes(f.size)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        );
      case 'failed':
        return (
          <>
            {title(`dropReceive.failed.${state.reason}.title`)}
            <p>{t(`dropReceive.failed.${state.reason}.body`)}</p>
          </>
        );
    }
  })();

  return (
    <main className={revealStyles.page}>
      <div className={revealStyles.stack}>
        <p className={ui.eyebrow}>{t('dropReceive.eyebrow')}</p>
        {body}
      </div>
    </main>
  );
}
